import React, { useRef, useState } from "react"
import { Paperclip, X } from "lucide-react"
import { Label } from "../../../ui/label"
import { Button } from "../../../ui/button"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../../ui/tooltip"
import { Info } from "lucide-react"
import { cn } from "../../../lib/utils"

/**
 * FileInput component - Styled file upload input that matches text input appearance
 *
 * @param {string} title - Input label
 * @param {string} ids - Input ID
 * @param {string} name - Input name
 * @param {string} accept - Accepted file types (e.g. ".csv")
 * @param {function} onChange - Change handler, receives the native event
 * @param {string} errorMsg - Error message
 * @param {boolean} disabled - Disabled state
 * @param {string} info - Tooltip info text
 * @param {boolean} show - Show tooltip
 * @param {string} className - Additional classes for the container
 * @param {string} placeholder - Placeholder text when no file is chosen
 */
const FileInput = ({
  title,
  ids,
  name,
  accept,
  onChange,
  errorMsg,
  disabled,
  info,
  show = true,
  className,
  placeholder = "No file chosen",
}) => {
  const inputRef = useRef(null)
  const [fileName, setFileName] = useState("")

  const handleChange = (e) => {
    const file = e.target.files?.[0]
    setFileName(file ? file.name : "")
    if (onChange) onChange(e)
  }

  const handleClear = () => {
    setFileName("")
    if (inputRef.current) inputRef.current.value = ""
    // fire a synthetic empty event so parent state clears too
    if (onChange) {
      const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype,
        "value"
      ).set
      nativeInputValueSetter.call(inputRef.current, "")
      inputRef.current.dispatchEvent(new Event("change", { bubbles: true }))
    }
  }

  return (
    <div className={cn("space-y-2", className)}>
      {title && (
        <Label htmlFor={ids} className="flex items-center gap-2">
          {title}
          {show && info && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-4 w-4 cursor-help text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>{info}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </Label>
      )}

      {/* Hidden native input */}
      <input
        ref={inputRef}
        id={ids}
        name={name}
        type="file"
        accept={accept}
        disabled={disabled}
        onChange={handleChange}
        className="sr-only"
        aria-label={title || placeholder}
      />

      {/* Custom single-row UI */}
      <div
        onClick={() => !disabled && inputRef.current?.click()}
        className={cn(
          "flex h-10 w-full items-center rounded-md border bg-background text-sm ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
          errorMsg ? "border-destructive" : "border-input",
          disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
        )}
      >
        {/* Filename display area */}
        <div className="flex flex-1 items-center gap-2 px-3 min-w-0">
          {fileName ? (
            <>
              <Paperclip className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="truncate text-foreground">{fileName}</span>
            </>
          ) : (
            <span className="truncate text-muted-foreground">{placeholder}</span>
          )}
        </div>

        {/* Clear button (only when file is selected) */}
        {fileName && !disabled && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); handleClear(); }}
            className="px-2 text-muted-foreground hover:text-foreground focus:outline-none"
            aria-label="Clear file"
          >
            <X className="h-4 w-4" />
          </button>
        )}

        {/* Browse button */}
        <Button
          type="button"
          variant="default"
          size="sm"
          disabled={disabled}
          className="m-1 h-8 shrink-0 rounded-sm px-3 text-xs"
          onClick={(e) => e.stopPropagation()}
        >
          Browse
        </Button>
      </div>

      {errorMsg && (
        <p className="text-sm text-destructive">{errorMsg}</p>
      )}
    </div>
  )
}

export default FileInput
